//frontend/src/actions/userActions.js
import axios from 'axios';
import { toast } from 'react-toastify'; // Add this import at the top

import {
    loginRequest, loginSuccess, loginFail, clearError,
    registerRequest, registerSuccess, registerFail,
    loadUserRequest, loadUserSuccess, loadUserFail,
    logoutSuccess, logoutFail,
    updateProfileRequest,
    updateProfileSuccess,
    updateProfileFail,
    updatePasswordRequest,
    updatePasswordSuccess,
    updatePasswordFail,
    forgotPasswordRequest,
    forgotPasswordSuccess,
    forgotPasswordFail,
    resetPasswordRequest,
    resetPasswordSuccess,
    resetPasswordFail
} from "../slices/authSlice";

// Helper function to handle and format errors
const handleError = (error, dispatch, failAction) => {
    console.error("API Error:", error);
    const message = error.response ? error.response.data.message : error.message;
    dispatch(failAction(message));
    return Promise.reject(error);
};

export const login = (email, password) => async (dispatch) => {
    try {
        dispatch(loginRequest());
        
        const config = {
            headers: { 'Content-Type': 'application/json' },
            withCredentials: true
        };
        
        const { data } = await axios.post('/api/v1/login', { email, password }, config);
        dispatch(loginSuccess(data));
        return Promise.resolve(data);
    } catch (error) {
        return handleError(error, dispatch, loginFail);
    }
};

export const clearAuthError = () => (dispatch) => {
    dispatch(clearError());
};

// Updated register function in userActions.js
export const register = (userData) => async (dispatch) => {
    try {
        dispatch(registerRequest());
        
        // Create custom config with longer timeout for file uploads
        const config = {
            headers: { 'Content-Type': 'multipart/form-data' },
            withCredentials: true,
            timeout: 60000 // 60 second timeout for uploads
        };
        
        const { data } = await axios.post('/api/v1/register', userData, config);
        
        // Check if there's a warning message about avatar upload
        if (data.message && data.message.includes('profile picture upload failed')) {
            toast.warning(data.message);
        } else {
            toast.success('Registration successful! Welcome to Meat Buddy.');
        }
        
        // On successful registration
        dispatch(registerSuccess(data));
        return Promise.resolve(data);
    } catch (error) {
        console.error("Registration error:", error);
        
        // Display appropriate error message
        const errorMessage = error.response?.data?.message || 'Registration failed. Please try again.';
        toast.error(errorMessage);
        
        dispatch(registerFail(errorMessage));
        return Promise.reject(error);
    }
};

// In userActions.js - loadUser function
export const loadUser = () => async (dispatch) => {
    try {
        dispatch(loadUserRequest());
        
        const { data } = await axios.get('/api/v1/myprofile', { withCredentials: true });
        dispatch(loadUserSuccess(data));
        return Promise.resolve(data);
    } catch (error) {
        // Handle 401 errors quietly since they're expected when not logged in
        if (error.response && error.response.status === 401) {
            dispatch(loadUserFail(null));
            console.log('User not authenticated, skipping profile load');
        } else {
            const errorMsg = error.response?.data?.message || 'Failed to load user profile';
            dispatch(loadUserFail(errorMsg));
            console.error('Error loading user profile:', error);
        }
        return Promise.reject(error);
    }
};

export const logout = () => async (dispatch) => {
    try {
        dispatch(clearAuthError()); // Clear any previous errors
        
        const { data } = await axios.get('/api/v1/logout', { withCredentials: true });
        if (data.success) {
            dispatch(logoutSuccess());
            toast.success('Logged out successfully');
            return Promise.resolve(data);
        } else {
            dispatch(logoutFail('Logout failed'));
            return Promise.reject(new Error('Logout failed'));
        }
    } catch (error) {
        return handleError(error, dispatch, logoutFail);
    }
};

export const updateProfile = (userData) => async (dispatch) => {
    try {
        dispatch(updateProfileRequest());
        
        const config = {
            headers: { 'Content-Type': 'multipart/form-data' },
            withCredentials: true,
            timeout: 60000 // 60 second timeout for uploads
        };
        
        const { data } = await axios.put('/api/v1/update', userData, config);
        dispatch(updateProfileSuccess(data));
        toast.success('Profile updated successfully');
        return Promise.resolve(data);
    } catch (error) {
        return handleError(error, dispatch, updateProfileFail);
    }
};

export const updatePassword = (formData) => async (dispatch) => {
    try {
        dispatch(updatePasswordRequest());
        
        const config = {
            headers: { 'Content-Type': 'application/json' },
            withCredentials: true
        };
        
        const { data } = await axios.put('/api/v1/password/change', formData, config);
        dispatch(updatePasswordSuccess());
        toast.success('Password updated successfully');
        return Promise.resolve(data);
    } catch (error) {
        return handleError(error, dispatch, updatePasswordFail);
    }
};

export const forgotPassword = (email) => async (dispatch) => {
    try {
        dispatch(forgotPasswordRequest());
        
        const config = {
            headers: {
                'Content-Type': 'application/json'
            },
            withCredentials: true
        };
        
        let formData = { email };
        if (typeof email === 'object' && email.get) {
            formData = { email: email.get('email') };
        }
        
        const { data } = await axios.post('/api/v1/password/forgot', formData, config);
        dispatch(forgotPasswordSuccess(data.message));
        toast.success(data.message || 'OTP sent successfully to your email');
        return Promise.resolve(data);
    } catch (error) {
        dispatch(forgotPasswordFail(error.response?.data?.message || 'Failed to send email'));
        toast.error(error.response?.data?.message || 'Failed to send email');
        return Promise.reject(error);
    }
};

// Verify OTP function
export const verifyOTP = (email, otp) => async (dispatch) => {
    try {
        dispatch({ type: 'VERIFY_OTP_REQUEST' });
        
        const config = {
            headers: {
                'Content-Type': 'application/json'
            },
            withCredentials: true
        };
        
        const { data } = await axios.post('/api/v1/password/verify-otp', { email, otp }, config);
        
        dispatch({
            type: 'VERIFY_OTP_SUCCESS',
            payload: data.message
        });
        
        toast.success(data.message || 'OTP verification successful');
        return Promise.resolve(data);
    } catch (error) {
        dispatch({
            type: 'VERIFY_OTP_FAIL',
            payload: error.response?.data?.message || 'OTP verification failed'
        });
        
        toast.error(error.response?.data?.message || 'OTP verification failed');
        return Promise.reject(error);
    }
};

export const resetPassword = (formData) => async (dispatch) => {
    try {
        dispatch(resetPasswordRequest());
        
        const config = {
            headers: {
                'Content-Type': 'application/json'
            },
            withCredentials: true
        };
        
        const data = {
            email: formData.email,
            otp: formData.otp,
            newPassword: formData.newPassword,
            confirmPassword: formData.confirmPassword
        };

        const { data: response } = await axios.post('/api/v1/password/reset', data, config);

        if (response.success) {
            dispatch(resetPasswordSuccess());
            toast.success('Password reset successfully. Please login with your new password.');
           
            // Redirect to login page after successful password reset
            setTimeout(() => {
                window.location.href = '/login';
            }, 2000);
            
            return Promise.resolve(response);
        }
    } catch (error) {
        dispatch(resetPasswordFail(error.response?.data?.message || 'Password reset failed'));
        toast.error(error.response?.data?.message || 'Password reset failed');
        return Promise.reject(error);
    }
};

// Function to handle updating the user by admin
export const updateUserByAdmin = (userId, userData) => async (dispatch) => {
    try {
        dispatch({ type: 'UPDATE_USER_BY_ADMIN_REQUEST' });
        
        const config = {
            headers: { 
                'Content-Type': userData instanceof FormData ? 'multipart/form-data' : 'application/json' 
            },
            withCredentials: true
        };
        
        const { data } = await axios.put(`/api/v1/admin/user/${userId}`, userData, config);
        
        dispatch({ 
            type: 'UPDATE_USER_BY_ADMIN_SUCCESS',
            payload: data
        });
        
        toast.success('User updated successfully');
        return Promise.resolve(data);
    } catch (error) {
        dispatch({ 
            type: 'UPDATE_USER_BY_ADMIN_FAIL',
            payload: error.response?.data?.message || 'Failed to update user'
        });
        
        toast.error(error.response?.data?.message || 'Failed to update user');
        return Promise.reject(error);
    }
};

// Function to create user by admin
export const createUserByAdmin = (userData) => async (dispatch) => {
    try {
        dispatch({ type: 'CREATE_USER_BY_ADMIN_REQUEST' });
        
        const config = {
            headers: { 'Content-Type': 'multipart/form-data' },
            withCredentials: true,
            timeout: 60000 // 60 second timeout for uploads
        };
        
        const { data } = await axios.post('/api/v1/admin/user/new', userData, config);
        
        dispatch({ 
            type: 'CREATE_USER_BY_ADMIN_SUCCESS',
            payload: data
        });
        
        toast.success('User created successfully');
        return Promise.resolve(data);
    } catch (error) {
        dispatch({ 
            type: 'CREATE_USER_BY_ADMIN_FAIL',
            payload: error.response?.data?.message || 'Failed to create user'
        });
        
        toast.error(error.response?.data?.message || 'Failed to create user');
        return Promise.reject(error);
    }
};

// Function to delete user by admin
export const deleteUserByAdmin = (userId) => async (dispatch) => {
    try {
        dispatch({ type: 'DELETE_USER_BY_ADMIN_REQUEST' });
        
        const { data } = await axios.delete(`/api/v1/admin/user/${userId}`, {
            withCredentials: true
        });
        
        dispatch({ 
            type: 'DELETE_USER_BY_ADMIN_SUCCESS',
            payload: data
        });
        
        toast.success('User deleted successfully');
        return Promise.resolve(data);
    } catch (error) {
        dispatch({ 
            type: 'DELETE_USER_BY_ADMIN_FAIL',
            payload: error.response?.data?.message || 'Failed to delete user'
        });
        
        toast.error(error.response?.data?.message || 'Failed to delete user');
        return Promise.reject(error);
    }
};

// Function to get all users (for admin)
export const getAllUsers = () => async (dispatch) => {
    try {
        dispatch({ type: 'GET_ALL_USERS_REQUEST' });
        
        const { data } = await axios.get('/api/v1/admin/users', {
            withCredentials: true
        });
        
        dispatch({ 
            type: 'GET_ALL_USERS_SUCCESS',
            payload: data.users
        });
        
        return Promise.resolve(data);
    } catch (error) {
        dispatch({ 
            type: 'GET_ALL_USERS_FAIL',
            payload: error.response?.data?.message || 'Failed to fetch users'
        });
        
        toast.error(error.response?.data?.message || 'Failed to fetch users');
        return Promise.reject(error);
    }
};

// Function to block/unblock user (for admin)
export const toggleBlockUser = (userId, isBlocked) => async (dispatch) => {
    try {
        dispatch({ type: 'TOGGLE_BLOCK_USER_REQUEST' });
        
        const { data } = await axios.put(
            `/api/v1/admin/user/${userId}/block`,
            {},
            { withCredentials: true }
        );
        
        dispatch({ 
            type: 'TOGGLE_BLOCK_USER_SUCCESS',
            payload: data
        });
        
        toast.success(data.message || `User ${isBlocked ? 'unblocked' : 'blocked'} successfully`);
        return Promise.resolve(data);
    } catch (error) {
        dispatch({ 
            type: 'TOGGLE_BLOCK_USER_FAIL',
            payload: error.response?.data?.message || 'Failed to update user status'
        });
        
        toast.error(error.response?.data?.message || 'Failed to update user status');
        return Promise.reject(error);
    }
};